// region header
//--------------------------------------------------------------------------------------------------
//
//  Description: Cluster Page
//
//  Copyright:    © 2025 Idox Software Limited.
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
// region Version 1.0.0.0
//    001   12.03.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   13.03.25 Sean Flook          GMSCM-1 Added code to handle when a users token has expired.
// endregion Version 1.0.0.0
//
//--------------------------------------------------------------------------------------------------
// endregion header

import React, { useCallback, useEffect, useRef, useState } from "react";

import useClusterQueryStore from "../context/ClusterStore";
import useUserQueryStore from "../context/UserStore";

import useClusters from "../hooks/useClusters";
import useAddCluster from "../hooks/useAddCluster";
import useUpdateCluster from "../hooks/useUpdateCluster";

import { ArraysEqual, loginStorageName, tokenStorageName, userStorageName } from "../utils/HelperUtils";
import { validateCluster } from "../utils/ClusterValidation";

import MultiTenantClusterGetDto, { UserApis } from "../dtos/multiTenantClusterDto";
import ClusterErrorType from "../models/clusterErrorType";

import { Backdrop, Box, Button, CircularProgress, Grid2, Link, Stack, Tab, Tabs, Typography } from "@mui/material";

import ClusterApiForm from "../forms/ClusterApiForm";
import ClusterForm from "../forms/ClusterForm";
import ClusterOrganisationForm from "../forms/ClusterOrganisationForm";
import ClusterTree from "../forms/ClusterTree";
import AddDialog, { AddVariant } from "../dialogs/AddDialog";
import ConfirmEditLossDialog from "../dialogs/ConfirmEditLossDialog";

import ErrorIcon from "@mui/icons-material/Error";
import SaveIcon from "@mui/icons-material/Save";

import { adsBlueA, adsLightGreyA, adsLightGreyB, adsMagenta, adsMidGreyA } from "../utils/ADSColour";
import { dataFormStyle, dataFormToolbarHeight, getSaveButtonStyle, toolbarStyle } from "../utils/ADSStyles";

interface ClusterPageProps {}

const ClusterPage: React.FC<ClusterPageProps> = () => {
  const displayName = useUserQueryStore((s) => s.userQuery.currentUser?.displayName);
  const logoff = useUserQueryStore((s) => s.logoff);

  const originalCluster = useClusterQueryStore((s) => s.clusterQuery.originalCluster);
  const newCluster = useClusterQueryStore((s) => s.clusterQuery.newCluster);
  const disableSave = useClusterQueryStore((s) => s.clusterQuery.disableSave);
  const revertChanges = useClusterQueryStore((s) => s.clusterQuery.revertChanges);
  const validationErrors = useClusterQueryStore((s) => s.clusterQuery.validationErrors);
  const setClusterItem = useClusterQueryStore((s) => s.setClusterItem);
  const setNewCluster = useClusterQueryStore((s) => s.setNewCluster);
  const setChanged = useClusterQueryStore((s) => s.setChanged);
  const setOriginalCluster = useClusterQueryStore((s) => s.setOriginalCluster);
  const setClusters = useClusterQueryStore((s) => s.setClusters);
  const setClearFilter = useClusterQueryStore((s) => s.setClearFilter);
  const setDisableSave = useClusterQueryStore((s) => s.setDisableSave);
  const setRevertChanges = useClusterQueryStore((s) => s.setRevertChanges);

  const { data, error, isLoading } = useClusters();

  const addCluster = useAddCluster((newData: MultiTenantClusterGetDto[]) => {
    setNewCluster(false);
    modifiedItems.current = [];
    updateClusters(newData);
    originalClusterData.current = newData;
    setEnableSave(false);
    setChanged(false);
    if (selectedItemId !== newSelectedItemId.current) handleItemSelectedEvent(newSelectedItemId.current);
  });

  const updateCluster = useUpdateCluster((updatedData: MultiTenantClusterGetDto[]) => {
    setNewCluster(false);
    modifiedItems.current = [];
    updateClusters(updatedData);
    originalClusterData.current = updatedData;
    setEnableSave(false);
    setChanged(false);
    if (selectedItemId !== newSelectedItemId.current) handleItemSelectedEvent(newSelectedItemId.current);
  });

  const [errorText, setErrorText] = useState<string | null>(null);
  const [clusterError, setClusterError] = useState<ClusterErrorType[] | undefined>(undefined);
  const [clusterData, setClusterData] = useState<MultiTenantClusterGetDto[]>([]);
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>("");
  const [clusterFormData, setClusterFormData] = useState<MultiTenantClusterGetDto | undefined>();
  const [apiFormData, setApiFormData] = useState<UserApis | undefined>();
  const [organisationFormData, setOrganisationFormData] = useState<string[] | undefined>();
  const [tabValue, setTabValue] = useState<number>(0);

  const [enableSave, setEnableSave] = useState<boolean>(false);
  const [haveErrors, setHaveErrors] = useState<boolean>(false);
  const [errorIds, setErrorIds] = useState<string[]>([]);

  const [tokenExpired, setTokenExpired] = useState<boolean>(false);

  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [openConformation, setOpenConformation] = useState<boolean>(false);

  const originalClusterData = useRef<MultiTenantClusterGetDto[]>([]);
  const addId = useRef<string | undefined>(undefined);

  const modifiedItems = useRef<string[]>([]);
  const newSelectedItemId = useRef<string | undefined>(undefined);
  const saveItemId = useRef<string | undefined>(undefined);

  const tabStyle = {
    alignItems: "flex-start",
    textTransform: "none",
    minHeight: `${dataFormToolbarHeight}px`,
    minWidth: "auto",
    px: 1,
  };

  const tabLabelStyle = (isSelected: boolean) => ({
    fontSize: "15px",
    fontWeight: isSelected ? 700 : 400,
    color: isSelected ? adsBlueA : adsMidGreyA,
  });

  useEffect(() => {
    if (error) setErrorText(error.message);
  }, [error]);

  /**
   * Event to handle when a node from the tree view is selected.
   *
   * @param nodeId The id of the selected node.
   */
  const handleItemSelectedEvent = useCallback(
    (nodeId: string | undefined): void => {
      const nodeIds: string[] = nodeId ? nodeId.split("|") : [];
      const changedDocument: boolean = !selectedItemId || selectedItemId.split("|")[0] !== nodeIds[0];
      newSelectedItemId.current = nodeId;

      if (changedDocument && selectedItemId && modifiedItems.current.includes(selectedItemId.split("|")[0])) {
        // Show a warning message that there are unsaved changes  and ask the user if they want to continue
        // If they say no, then return and do not change the selected item
        // If they say yes, then continue with the change
        setOpenConformation(true);
      } else {
        if (nodeId) {
          if (nodeIds && nodeIds.length > 0) {
            let selectedCluster: MultiTenantClusterGetDto | undefined = clusterData.find((x) => x.id === nodeIds[0]);
            let selectedTab: number = 0;

            if (changedDocument) {
              const clonedCluster = structuredClone(selectedCluster);
              if (clonedCluster) setOriginalCluster(clonedCluster);
            }

            setSelectedItemId(nodeId);
            setClusterItem(nodeId);

            if (selectedCluster && nodeIds.length > 1) {
              if (nodeIds[1] === "user-apis") selectedTab = 1;
              if (nodeIds[1] === "organisations") selectedTab = 2;
            }

            setClusterFormData(undefined);
            setApiFormData(undefined);
            setOrganisationFormData(undefined);

            if (selectedCluster) {
              setClusterFormData(selectedCluster);
              setApiFormData(selectedCluster.userApis);
              setOrganisationFormData(selectedCluster.organisations);
              setTabValue(selectedTab);
            }
          }
        } else {
          setSelectedItemId(undefined);
          setClusterItem(undefined);
          newSelectedItemId.current = undefined;
          setClusterFormData(undefined);
          setApiFormData(undefined);
          setOrganisationFormData(undefined);
          setTabValue(0);
        }
      }
    },
    [clusterData, selectedItemId, setOriginalCluster, setClusterItem]
  );

  const handleAddItem = (type: string, id?: string | undefined): void => {
    switch (type) {
      case "cluster":
        handleAddCluster();
        break;

      case "organisation":
        if (id) {
          handleAddOrganisation(id);
        }
        break;

      default:
        break;
    }
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
    setTabValue(newValue);
  };

  const handleViewIssuesClick = (): void => {
    if (!selectedItemId || !clusterError || clusterError.length === 0) return;

    const selectedClusterId = selectedItemId.split("|")[0];
    const selectedClusterErrors = clusterError.filter((x) => x.id === selectedClusterId);
    if (selectedClusterErrors.length === 0) return;

    const orderedFields = ["name", "application", "imanage", "settings", "lookups", "organisation"];
    const firstError =
      orderedFields
        .map((field) => selectedClusterErrors.find((x) => x.field === field))
        .find((error) => !!error) ?? selectedClusterErrors[0];

    if (!firstError) return;

    let targetId: string | undefined = undefined;

    switch (firstError.field) {
      case "name":
        setTabValue(0);
        targetId = "ads-text-textfield-name";
        break;

      case "application":
        setTabValue(0);
        targetId = "ads-text-textfield-application";
        break;

      case "imanage":
      case "settings":
      case "lookups":
        setTabValue(1);
        targetId = `ads-text-textfield-${firstError.field}`;
        break;

      case "organisation":
        setTabValue(2);
        targetId = "cluster_organisation_0";
        break;

      default:
        setTabValue(0);
        break;
    }

    const focusById = (id: string, retries: number = 5): void => {
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ block: "center" });
        if ("focus" in element) {
          (element as HTMLElement).focus();
        }
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
          element.select();
        }
        return;
      }

      if (retries > 0) {
        window.setTimeout(() => focusById(id, retries - 1), 50);
      }
    };

    if (targetId) {
      const focusTargetId: string = targetId;
      window.setTimeout(() => focusById(focusTargetId), 0);
    }
  };

  /**
   * Method to maintain the list of modified items.
   *
   * @param {string} docId The id of the document.
   * @param {boolean} hasChanged A flag indicating if the document has changed.
   */
  const updateModifiedItems = (docId: string, hasChanged: boolean): void => {
    const newList: string[] = [...modifiedItems.current];
    const index: number = newList.indexOf(docId);
    if (index === -1 && hasChanged) {
      newList.push(docId);
    } else if (!hasChanged && index !== -1) {
      newList.splice(index, 1);
    }
    modifiedItems.current = newList;
    setChanged(modifiedItems.current.length > 0);
    setEnableSave(newList.length > 0);
  };

  /**
   * Method to display the cluster form.
   *
   * @param {MultiTenantClusterGetDto} cluster The cluster to display.
   */
  const displayClusterForm = (cluster: MultiTenantClusterGetDto): void => {
    setClusterFormData(cluster);
    setApiFormData(cluster.userApis);
    setOrganisationFormData(cluster.organisations);
    setTabValue(0);
  };

  /**
   * Method to add a new cluster record.
   */
  const handleAddCluster = (): void => {
    function generateUniqueId() {
      return Math.random().toString(36).substring(2, 9);
    }

    function isIdInConfigDocData(id: string) {
      return clusterData.find((x) => x.id === id);
    }

    setNewCluster(true);

    let newId = generateUniqueId();
    while (isIdInConfigDocData(newId)) {
      newId = generateUniqueId();
    }

    const newCluster: MultiTenantClusterGetDto = {
      id: newId,
      configType: "MTClusterConfig",
      version: 1,
      name: "_New Cluster",
      application: "",
      created: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      createdBy: displayName ?? "",
      lastUpdatedBy: displayName ?? "",
      userApis: {
        iManage: "",
        Settings: "",
        Lookups: "",
      },
      organisations: [],
    };

    const updatedClusters = [...clusterData];
    updatedClusters.push(newCluster);
    updateClusters(updatedClusters);

    newSelectedItemId.current = newCluster.id;
    setOriginalCluster(newCluster!);
    setSelectedItemId(newCluster.id);
    setClusterItem(newCluster.id);
    displayClusterForm(newCluster);
    updateModifiedItems(newCluster.id, true);
    setChanged(true);
    setEnableSave(true);
    document.getElementById(newCluster.id!)?.scrollIntoView();
    document.getElementById(newCluster.id!)?.focus();
  };

  /**
   * Method to handle the event when a new organisation is added.
   *
   * @param {string} id The id of the currently selected node.
   */
  const handleAddOrganisation = (id: string): void => {
    if (id) {
      addId.current = id;
      setOpenAddDialog(true);
    }
  };

  /**
   * Method to save the cluster record.
   *
   * @param {string} nodeId The id of the node to save.
   * @return {Promise<void>}
   */
  const handleSaveClicked = async (nodeId: string): Promise<void> => {
    if (nodeId) {
      const nodeIds: string[] = nodeId.split("|");
      saveItemId.current = nodeIds[0];
      const currentCluster: MultiTenantClusterGetDto | undefined = clusterData.find((x) => x.id === nodeIds[0]);
      const currentCluster2 = originalCluster;

      console.log("[SF] handleSaveClicked", currentCluster, currentCluster2);

      setClusterError(undefined);

      const validationErrors: ClusterErrorType[] | undefined = validateCluster(clusterData, currentCluster);

      if (!validationErrors || validationErrors.length === 0) {
        if (currentCluster) {
          if (newCluster) {
            addCluster.mutate(currentCluster);
          } else {
            updateCluster.mutate(currentCluster);
          }
        }
      } else {
        setClusterError(validationErrors);
      }
    }
  };

  /**
   * Method to handle the change of the cluster data.
   *
   * @param data The new cluster data.
   */
  const handleClusterDataChange = (data: MultiTenantClusterGetDto): void => {
    setClusterFormData(data);
    const updatedCluster = clusterData
      .map((x) => (x.id === data.id ? data : x))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" }));
    setClusterData(updatedCluster);
    const updatedCurrentCluster = updatedCluster.find((x) => x.id === data.id);
    const validationErrors: ClusterErrorType[] | undefined = validateCluster(updatedCluster, updatedCurrentCluster);
    setClusterError(validationErrors);
    const clusterChanged = !ArraysEqual(
      originalClusterData.current.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
      ),
      updatedCluster
    );
    updateModifiedItems(data.id, clusterChanged);
    setEnableSave(clusterChanged);
  };

  /**
   * Method to handle the change of the api data.
   *
   * @param {UserApis} data The new api data.
   */
  const handleApiDataChange = (data: UserApis): void => {
    if (selectedItemId) {
      const currentCluster: MultiTenantClusterGetDto | undefined = clusterData.find(
        (x) => x.id === selectedItemId.split("|")[0]
      );

      if (currentCluster) {
        const newCluster: MultiTenantClusterGetDto = {
          id: currentCluster.id,
          configType: currentCluster.configType,
          version: currentCluster.version,
          name: currentCluster.name,
          application: currentCluster.application,
          created: currentCluster.created,
          lastUpdated: currentCluster.lastUpdated,
          createdBy: currentCluster.createdBy,
          lastUpdatedBy: currentCluster.lastUpdatedBy,
          userApis: data,
          organisations: currentCluster.organisations,
        };

        const updatedCluster = clusterData.map((x) => [newCluster].find((rec) => rec.name === x.name) || x);

        setClusterData(updatedCluster);
        const validationErrors: ClusterErrorType[] | undefined = validateCluster(updatedCluster, newCluster);
        setClusterError(validationErrors);
        const clusterChanged = !ArraysEqual(
          originalClusterData.current.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
          ),
          updatedCluster
        );
        setApiFormData(data);
        updateModifiedItems(newCluster.id, clusterChanged);
        setChanged(clusterChanged);
        setEnableSave(clusterChanged);
      }
    }
  };

  /**
   * Method to delete the organisation record.
   *
   * @param name The id for the organisation record that should be deleted.
   */
  const handleDeleteOrganisationRecord = (name: string): void => {
    if (selectedItemId && name) {
      const currentCluster: MultiTenantClusterGetDto | undefined = clusterData.find(
        (x) => x.id === selectedItemId.split("|")[0]
      );

      if (currentCluster) {
        const newCluster: MultiTenantClusterGetDto = {
          id: currentCluster.id,
          configType: currentCluster.configType,
          version: currentCluster.version,
          name: currentCluster.name,
          application: currentCluster.application,
          created: currentCluster.created,
          lastUpdated: currentCluster.lastUpdated,
          createdBy: currentCluster.createdBy,
          lastUpdatedBy: currentCluster.lastUpdatedBy,
          userApis: currentCluster.userApis,
          organisations: currentCluster.organisations.filter((x) => x !== name),
        };

        const updatedCluster = clusterData.map((x) => [newCluster].find((rec) => rec.name === x.name) || x);

        setClusterData(updatedCluster);
        const validationErrors: ClusterErrorType[] | undefined = validateCluster(updatedCluster, newCluster);
        setClusterError(validationErrors);
        const clusterChanged = !ArraysEqual(
          originalClusterData.current.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
          ),
          updatedCluster
        );
        setOrganisationFormData(newCluster.organisations);
        updateModifiedItems(newCluster.id, clusterChanged);
        setChanged(clusterChanged);
        setEnableSave(clusterChanged);
      }
    }
  };

  /**
   * Method to handle the change of the organisation data.
   *
   * @param {string[]} data The updated organisations.
   */
  const handleOrganisationDataChange = (data: string[]): void => {
    if (selectedItemId) {
      const currentCluster: MultiTenantClusterGetDto | undefined = clusterData.find(
        (x) => x.id === selectedItemId.split("|")[0]
      );

      if (currentCluster) {
        const newCluster: MultiTenantClusterGetDto = {
          id: currentCluster.id,
          configType: currentCluster.configType,
          version: currentCluster.version,
          name: currentCluster.name,
          application: currentCluster.application,
          created: currentCluster.created,
          lastUpdated: currentCluster.lastUpdated,
          createdBy: currentCluster.createdBy,
          lastUpdatedBy: currentCluster.lastUpdatedBy,
          userApis: currentCluster.userApis,
          organisations: data,
        };

        const updatedCluster = clusterData.map((x) => [newCluster].find((rec) => rec.name === x.name) || x);

        setClusterData(updatedCluster);
        setOrganisationFormData(data);
        const validationErrors: ClusterErrorType[] | undefined = validateCluster(updatedCluster, newCluster);
        setClusterError(validationErrors);
        const clusterChanged = !ArraysEqual(
          originalClusterData.current.sort((a, b) =>
            a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
          ),
          updatedCluster
        );
        updateModifiedItems(newCluster.id, clusterChanged);
        setChanged(clusterChanged);
        setEnableSave(clusterChanged);
      }
    }
  };

  /**
   * Method to handle the event when the add dialog is closed.
   */
  const handleAddClose = (): void => {
    setOpenAddDialog(false);
  };

  /**
   * Method to handle the event when the add dialog is done.
   *
   * @param {string} org The name of the organisation to add.
   */
  const handleAddDone = (org: string): void => {
    const currentCluster = clusterData.find((x) => x.id === addId.current);
    if (currentCluster) {
      if (org && !currentCluster.organisations.includes(org)) {
        const newCluster: MultiTenantClusterGetDto = {
          id: currentCluster.id,
          configType: currentCluster.configType,
          version: currentCluster.version,
          name: currentCluster.name,
          application: currentCluster.application,
          created: currentCluster.created,
          lastUpdated: currentCluster.lastUpdated,
          createdBy: currentCluster.createdBy,
          lastUpdatedBy: currentCluster.lastUpdatedBy,
          userApis: currentCluster.userApis,
          organisations: [...currentCluster.organisations, org],
        };

        const updatedCluster = clusterData.map((x) => [newCluster].find((rec) => rec.name === x.name) || x);

        setClusterData(updatedCluster);
        setOrganisationFormData(newCluster.organisations);
        updateModifiedItems(currentCluster.id, true);
        setChanged(true);
        const newClusterError = clusterError?.filter((x) => x.field !== "organisation");
        setClusterError(newClusterError);
        setEnableSave(true);
      }
      setOpenAddDialog(false);
    }
  };

  /**
   * Method to handle the event when the user chooses to save the changes made to the current document.
   */
  const handleSaveChanges = (): void => {
    setOpenConformation(false);
    handleSaveClicked(selectedItemId!);
  };

  /**
   * Method to update the cluster data.
   *
   * @param {MultiTenantClusterGetDto[]} clusters The new cluster data.
   */
  const updateClusters = useCallback(
    (clusters: MultiTenantClusterGetDto[]): void => {
      setClusterData(clusters);
      originalClusterData.current = clusters;
      setClusters(clusters);
    },
    [setClusters]
  );

  /**
   * Method to handle the event when the user chooses to dispose of any changes made to the current document.
   */
  const handleAllowDispose = useCallback((): void => {
    setOpenConformation(false);
    let updatedClusters: MultiTenantClusterGetDto[] = [...clusterData];
    if (selectedItemId?.split("|")[0] === "new") {
      updatedClusters = clusterData.filter((x) => x.id !== "new");
    } else {
      const currentCluster: MultiTenantClusterGetDto | undefined = clusterData.find(
        (x) => x.id === selectedItemId?.split("|")[0]
      );
      if (currentCluster && originalCluster) {
        const index: number = updatedClusters.findIndex((x) => x.id === selectedItemId?.split("|")[0]);
        updatedClusters[index] = originalCluster;

        if (!!clusterFormData) {
          setClusterFormData(originalCluster);
        } else if (!!apiFormData) {
          setApiFormData(originalCluster.userApis);
        } else if (!!organisationFormData) {
          setOrganisationFormData(originalCluster.organisations);
        }
      }
    }

    updateClusters(updatedClusters);
    originalClusterData.current = updatedClusters;
    modifiedItems.current = [];
    setEnableSave(false);
    setClusterError(undefined);
    handleItemSelectedEvent(newSelectedItemId.current);
    document.getElementById(newSelectedItemId.current!)?.scrollIntoView();
    setChanged(false);
  }, [
    originalCluster,
    clusterData,
    selectedItemId,
    updateClusters,
    handleItemSelectedEvent,
    clusterFormData,
    apiFormData,
    organisationFormData,
    setChanged,
  ]);

  /**
   * Method to handle the event when the user cancels moving away from the dialog.
   */
  const handleCancelMoveAway = (): void => {
    setOpenConformation(false);
    document.getElementById(selectedItemId!)?.scrollIntoView();
    document.getElementById(selectedItemId!)?.focus();
    setClearFilter(true);
  };

  useEffect(() => {
    if (!isLoading) {
      if (data) {
        setClusterData(data);
        originalClusterData.current = data;
        setEnableSave(false);
      } else {
        originalClusterData.current = [];
      }
    }
  }, [isLoading, data]);

  useEffect(() => {
    setHaveErrors(!!errorText || (clusterError?.length ?? 0) > 0);

    if (clusterError && clusterError.length > 0 && Array.isArray(clusterError)) {
      const ids: string[] = clusterError.map((x) => x.id);
      setErrorIds(ids);
    }
  }, [errorText, clusterError]);

  useEffect(() => {
    if (disableSave) {
      setEnableSave(false);
      setDisableSave(false);
    }

    if (revertChanges) {
      handleAllowDispose();
      setRevertChanges(false);
    }
  }, [disableSave, revertChanges, setDisableSave, handleAllowDispose, setRevertChanges]);

  useEffect(() => {
    setClusterError(validationErrors);
  }, [validationErrors]);

  useEffect(() => {
    if (tokenExpired) {
      logoff();
      localStorage.removeItem(tokenStorageName);
      localStorage.removeItem(loginStorageName);
      localStorage.removeItem(userStorageName);
      setTokenExpired(false);
    }
  }, [tokenExpired, logoff]);

  useEffect(() => {
    if (addCluster.error) {
      setClusterError([{ id: saveItemId.current!, field: "name", errors: [addCluster.error.message] }]);
    }
  }, [addCluster.error]);

  useEffect(() => {
    if (updateCluster.error) {
      setClusterError([{ id: saveItemId.current!, field: "name", errors: [updateCluster.error.message] }]);
    }
  }, [updateCluster.error]);

  return isLoading ? (
    <div>
      <Backdrop open={isLoading}>
        Loading data, please wait...
        <CircularProgress color="inherit" />
      </Backdrop>
    </div>
  ) : (
    <>
      <Box sx={dataFormStyle("ADSClusterPageControl")}>
        <Grid2 container justifyContent="flex-start" spacing={0}>
          <Grid2 size={3}>
            <ClusterTree
              data={clusterData}
              selectedItem={selectedItemId}
              haveErrors={haveErrors}
              errorIds={errorIds}
              onItemSelected={handleItemSelectedEvent}
              onAddItem={handleAddItem}
            />
          </Grid2>
          <Grid2 size={9}>
            <Box
              sx={{
                ...toolbarStyle,
                height: `${dataFormToolbarHeight}px`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              {clusterFormData ? (
                <Tabs
                  value={tabValue}
                  onChange={handleTabChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  selectionFollowsFocus
                  aria-label="cluster-tabs"
                  sx={{
                    minHeight: `${dataFormToolbarHeight}px`,
                    "& .MuiTabs-indicator": {
                      display: "flex",
                      justifyContent: "center",
                      backgroundColor: "transparent",
                    },
                    "& .MuiTabs-indicatorSpan": {
                      width: "70%",
                      backgroundColor: adsBlueA,
                      height: "2px",
                    },
                  }}
                  TabIndicatorProps={{
                    children: <span className="MuiTabs-indicatorSpan" />,
                  }}
                >
                  <Tab sx={tabStyle} label={<span style={tabLabelStyle(tabValue === 0)}>Details</span>} />
                  <Tab sx={tabStyle} label={<span style={tabLabelStyle(tabValue === 1)}>APIs</span>} />
                  <Tab sx={tabStyle} label={<span style={tabLabelStyle(tabValue === 2)}>Organisations</span>} />
                </Tabs>
              ) : (
                <Box />
              )}
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
                spacing={1}
                sx={{ ml: "12px" }}
              >
                {clusterFormData && haveErrors && (
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mr: 1 }}>
                    <ErrorIcon sx={{ color: adsMagenta }} />
                    <Typography sx={{ color: adsMagenta, fontWeight: 700, fontSize: "15px" }}>
                      Edits in progress
                    </Typography>
                    <Link
                      component="button"
                      underline="always"
                      onClick={handleViewIssuesClick}
                      sx={{ color: adsMagenta, fontWeight: 700, fontSize: "15px" }}
                    >
                      View issues
                    </Link>
                  </Stack>
                )}
                <Button
                  id="property-save-button"
                  sx={getSaveButtonStyle(haveErrors)}
                  variant="text"
                  startIcon={!haveErrors ? <SaveIcon /> : <ErrorIcon />}
                  disabled={!enableSave}
                  onClick={() => handleSaveClicked(selectedItemId!)}
                >
                  Save
                </Button>
              </Stack>
            </Box>
            {clusterFormData && (
              <Box
                sx={{
                  height: "3px",
                  backgroundColor: adsLightGreyA,
                  borderBottom: `1px solid ${adsLightGreyB}`,
                }}
              />
            )}
            {clusterFormData && (
              <>
                {tabValue === 0 && (
                  <ClusterForm data={clusterFormData} errors={clusterError} onDataChange={handleClusterDataChange} />
                )}
                {tabValue === 1 && apiFormData && (
                  <ClusterApiForm data={apiFormData} errors={clusterError} onDataChange={handleApiDataChange} />
                )}
                {tabValue === 2 && organisationFormData && (
                  <ClusterOrganisationForm
                    data={organisationFormData}
                    errors={clusterError}
                    onDataChange={handleOrganisationDataChange}
                    onDeleteOrganisationRecord={handleDeleteOrganisationRecord}
                    onAddOrganisationRecord={() => handleAddOrganisation(selectedItemId?.split("|")[0] ?? "")}
                  />
                )}
              </>
            )}
          </Grid2>
        </Grid2>
      </Box>
      <AddDialog
        open={openAddDialog}
        variant={AddVariant.clusterOrganisation}
        onClose={handleAddClose}
        onDone={handleAddDone}
      />
      <ConfirmEditLossDialog
        isOpen={openConformation}
        title="Unsaved changes"
        message="You have made changes to this record, do you want to keep them or discard them?"
        saveText="Keep"
        handleSaveClick={handleSaveChanges}
        handleDisposeClick={handleAllowDispose}
        handleReturnClick={handleCancelMoveAway}
      />
    </>
  );
};

export default ClusterPage;
